/* eslint-disable no-param-reassign */
import type { Data } from '../specProcessor';
import type { StandardChartLayerSpec } from '../../../core/layer';
import { checkFieldType, createFieldValidator, isFieldValid } from './helper';
import type { IEncodingVersionAdapter } from '../../../core/encodingCompatibility';
import * as utils from '../../../util';

export interface ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

export type BaseSpec = {
  layers: Partial<StandardChartLayerSpec>[];
};

export interface IFieldTypeValidator {
  field: keyof StandardChartLayerSpec['encoding'];
  type: 'string' | 'date' | 'number' | 'array' | ('string' | 'date')[];
  required?: boolean;
  array?: boolean;
}

export interface IEncodingField {
  field: keyof StandardChartLayerSpec['encoding'];
  array?: boolean;
  required?: boolean;
}

/**
 * 检查单个字段的类型是否有效
 * @param values 数据值
 * @param field 字段名
 * @param layerType 图层类型
 * @param fieldName 编码字段名
 * @param type 期望的数据类型
 * @returns 是否有效
 */
function validateFieldType(
  values: Record<string, unknown>[],
  field: string,
  layerType: string,
  fieldName: string,
  type: 'string' | 'date' | 'number' | 'array' | ('string' | 'date')[]
): boolean {
  // 处理多类型情况
  if (Array.isArray(type)) {
    const isTypeValid = type.some(t => checkFieldType(values, field, t));

    if (!isTypeValid) {
      utils.logger.warn(
        'warning',
        `The "${fieldName}" field of the "${layerType}" layer must be one of: ${type.join(', ')}`
      );
      return false;
    }
    return true;
  }

  // 处理单类型情况
  if (!checkFieldType(values, field, type)) {
    utils.logger.warn(
      'warning',
      `The "${fieldName}" field of the "${layerType}" layer must be a ${type}`
    );
    return false;
  }

  return true;
}

/**
 * 创建通用的数据类型验证函数
 * @param layerType 图层类型
 * @param fieldValidators 字段验证配置
 * @returns 验证函数
 */
export function createDataTypeValidator(layerType: string, fieldValidators: IFieldTypeValidator[]) {
  return function validateDataTypes(
    layer: StandardChartLayerSpec & ILayerTypeSpec,
    data: Data
  ): boolean {
    const { encoding } = layer;
    const dataIndex = layer.dataIndex || 0;
    const values = data[dataIndex]?.values || [];

    if (!values.length) {
      return false;
    }

    let isValid = true;

    // 对每个验证器进行验证
    fieldValidators.forEach(validator => {
      const { field, type, required = true } = validator;

      // 跳过不需要验证的字段
      if ((!required && !encoding[field]) || !encoding[field]) {
        return;
      }

      const fieldValue = encoding[field];
      const fieldValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];

      // 验证字段值
      fieldValues.forEach(singleField => {
        if (!validateFieldType(values, singleField as string, layerType, field as string, type)) {
          isValid = false;
        }
      });
    });

    return isValid;
  };
}

/**
 * 创建通用编码验证函数
 * @param requiredFields 必需字段配置
 * @returns 验证函数
 */
export function createEncodingValidator(requiredFields: IEncodingField[]) {
  return function validateEncoding(layer: StandardChartLayerSpec): boolean {
    let isValid = true;

    requiredFields.forEach(({ field, array = false, required = true }) => {
      if (required && !createFieldValidator(field)(layer, array)) {
        isValid = false;
      }

      if (!array && field !== 'y' && !isFieldValid(layer, field)) {
        isValid = false;
      }
    });

    return isValid;
  };
}

/**
 * 创建通用图层处理器
 * @param layerType - 图层类型
 * @param validateEncoding - 编码验证函数
 * @param validateDataTypes - 数据类型验证函数
 * @param additionalChecks - 额外验证函数（可选）
 * @param encodingAdapters - 编码适配器数组（可选）
 * @returns 图层处理函数
 */
export function createLayerProcessor<T extends StandardChartLayerSpec & ILayerTypeSpec>(
  layerType: string,
  validateEncoding: (layer: T) => boolean,
  validateDataTypes: (layer: T, data: Data) => boolean,
  additionalChecks?: (layer: T, data: Data) => void,
  encodingAdapters?: IEncodingVersionAdapter[]
) {
  return function processor(data: Data, spec: BaseSpec): boolean {
    let isValid = true;
    const { layers = [] } = spec;

    if (layers.every(layer => layer.type !== layerType)) {
      return isValid;
    }

    layers
      .filter((layer): layer is T => layer.type === layerType)
      .forEach(layer => {
        // 应用编码适配器
        if (encodingAdapters && layer.encoding) {
          const matchedAdapter = encodingAdapters.find(adapter =>
            adapter.isMatch(layer.encoding as Record<string, unknown>)
          );

          if (matchedAdapter) {
            const adaptedEncoding = matchedAdapter.transform(
              layer.encoding as Record<string, unknown>
            );
            layer.encoding = adaptedEncoding as T['encoding'];

            // 记录适配信息
            utils.logger.info(
              'info',
              `Applied encoding adapter "${matchedAdapter.version}" for layer type "${layerType}"`
            );
          }
        }

        if (!validateEncoding(layer)) {
          isValid = false;
          layer.__isEncodingValid = false;
        }

        if (!validateDataTypes(layer, data)) {
          isValid = false;
          layer.__isEncodingValid = false;
          utils.logger.warn('warning', `Data type check failed for "${layerType}" layer`);
        }

        if (additionalChecks) {
          additionalChecks(layer, data);
        }
      });

    return isValid;
  };
}
